import { useState } from 'react';

import { ensureDefined, invariant } from '@e-socna/utils';
import { validateResolverReturnType } from '@e-socna/resolvers';
import { ResolverEnum } from '@e-socna/data-layer';
import type { ResolverReturnTypeId } from '@e-socna/data-layer';

import { checkIfIsId } from '../../../utils/check-if-is-id.util';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { RESOLVER_HOOK_MAP } from '../resolvers/configs/resolver-registry.config';

interface Params {
  readonly enabled?: boolean;
}

/**
 * Hook to invoke required resolvers based on template variables.
 *
 * NOTE: Due to React Rules of Hooks, all hooks must be called unconditionally.
 * The RESOLVER_HOOK_MAP from resolver-registry.config.ts is the single source
 * of truth for all resolvers. This file uses that registry to ensure consistency.
 *
 * Resolvers now access data directly from contexts (ResolverContext, MissionContext, etc.)
 * instead of receiving parameters.
 *
 * When adding a new resolver:
 * 1. Add it to RESOLVER_REGISTRY in resolver-registry.config.ts
 * 2. Add the hook call below (following the existing pattern)
 * 3. Add it to the allResolvers mapping
 */
export const useRequiredResolvers = ({ enabled = true }: Readonly<Params>) => {
  const [requiredCommands, setRequiredCommands] = useState<
    readonly ResolverEnum[] | null
  >(null);

  const {
    currenciesIdMap,
    resolversCodeMap,
    resolverReturnTypesIdMap,
    unitsIdMap,
    vatsIdMap,
  } = useReferencesContext();

  // All resolver hooks called unconditionally (React requirement)
  // Source of truth: RESOLVER_HOOK_MAP in resolver-registry.config.ts
  const dateCourante = RESOLVER_HOOK_MAP[ResolverEnum.DateCourante]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DateCourante)
    ),
  });

  const dateHeureCourante = RESOLVER_HOOK_MAP[ResolverEnum.DateHeureCourante]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DateHeureCourante)
    ),
  });

  const devisAcceptationCgv = RESOLVER_HOOK_MAP[
    ResolverEnum.DevisAcceptationCgv
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisAcceptationCgv)
    ),
  });

  const devisInterlocuteur = RESOLVER_HOOK_MAP[ResolverEnum.DevisInterlocuteur](
    {
      enabled: !!(
        enabled && requiredCommands?.includes(ResolverEnum.DevisInterlocuteur)
      ),
    },
  );

  const devisEmetteur = RESOLVER_HOOK_MAP[ResolverEnum.DevisEmetteur]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisEmetteur)
    ),
  });

  const devisHonoraires = RESOLVER_HOOK_MAP[ResolverEnum.DevisHonoraires]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisHonoraires)
    ),
  });

  const devisLieuDate = RESOLVER_HOOK_MAP[ResolverEnum.DevisLieuDate]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisLieuDate)
    ),
  });

  const devisAvecModalites = RESOLVER_HOOK_MAP[ResolverEnum.DevisAvecModalites](
    {
      enabled: !!(
        enabled && requiredCommands?.includes(ResolverEnum.DevisAvecModalites)
      ),
    },
  );

  const devisAvecNota = RESOLVER_HOOK_MAP[ResolverEnum.DevisAvecNota]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisAvecNota)
    ),
  });

  const devisModalites = RESOLVER_HOOK_MAP[ResolverEnum.DevisModalites]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisModalites)
    ),
  });

  const devisNota = RESOLVER_HOOK_MAP[ResolverEnum.DevisNota]({
    enabled: !!(enabled && requiredCommands?.includes(ResolverEnum.DevisNota)),
  });

  const devisObjet = RESOLVER_HOOK_MAP[ResolverEnum.DevisObjet]({
    enabled: !!(enabled && requiredCommands?.includes(ResolverEnum.DevisObjet)),
  });

  const devisPolitesseIntro = RESOLVER_HOOK_MAP[
    ResolverEnum.DevisPolitesseIntro
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisPolitesseIntro)
    ),
  });

  const devisPolitesseOutro = RESOLVER_HOOK_MAP[
    ResolverEnum.DevisPolitesseOutro
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisPolitesseOutro)
    ),
  });

  const devisPrestations = RESOLVER_HOOK_MAP[ResolverEnum.DevisPrestations]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisPrestations)
    ),
  });

  const devisPrestationsTableau = RESOLVER_HOOK_MAP[
    ResolverEnum.DevisPrestationsTableau
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(ResolverEnum.DevisPrestationsTableau)
    ),
  });

  const devisReference = RESOLVER_HOOK_MAP[ResolverEnum.DevisReference]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisReference)
    ),
  });

  const devisReglement = RESOLVER_HOOK_MAP[ResolverEnum.DevisReglement]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisReglement)
    ),
  });

  const devisSignatureGerant = RESOLVER_HOOK_MAP[
    ResolverEnum.DevisSignatureGerant
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DevisSignatureGerant)
    ),
  });

  const docIdentifiant = RESOLVER_HOOK_MAP[ResolverEnum.DocIdentifiant]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DocIdentifiant)
    ),
  });

  const docIncrementGeneration = RESOLVER_HOOK_MAP[
    ResolverEnum.DocIncrementGeneration
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DocIncrementGeneration)
    ),
  });

  const docNombreTotalPages = RESOLVER_HOOK_MAP[
    ResolverEnum.DocNombreTotalPages
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DocNombreTotalPages)
    ),
  });

  const docTableDesMatieres = RESOLVER_HOOK_MAP[
    ResolverEnum.DocTableDesMatieres
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.DocTableDesMatieres)
    ),
  });

  const docTitre = RESOLVER_HOOK_MAP[ResolverEnum.DocTitre]({
    enabled: !!(enabled && requiredCommands?.includes(ResolverEnum.DocTitre)),
  });

  const missionTitre = RESOLVER_HOOK_MAP[ResolverEnum.MissionTitre]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.MissionTitre)
    ),
  });

  const sigCarteLieuMission = RESOLVER_HOOK_MAP[
    ResolverEnum.SigCarteLieuMission
  ]({
    enabled: !!(
      enabled && requiredCommands?.includes(ResolverEnum.SigCarteLieuMission)
    ),
  });

  const sigDistanceArrondieMissionAgenceEnKmAuPlusCourt = RESOLVER_HOOK_MAP[
    ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusCourt
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(
        ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusCourt,
      )
    ),
  });

  const sigDistanceArrondieMissionAgenceEnKmAuPlusLong = RESOLVER_HOOK_MAP[
    ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusLong
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(
        ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusLong,
      )
    ),
  });

  const sigDistanceArrondieMissionAgenceEnKmAuPlusRapide = RESOLVER_HOOK_MAP[
    ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusRapide
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(
        ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusRapide,
      )
    ),
  });

  const sigDistanceExacteMissionAgenceEnKmAuPlusCourt = RESOLVER_HOOK_MAP[
    ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusCourt
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(
        ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusCourt,
      )
    ),
  });

  const sigDistanceExacteMissionAgenceEnKmAuPlusLong = RESOLVER_HOOK_MAP[
    ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusLong
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(
        ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusLong,
      )
    ),
  });

  const sigDistanceExacteMissionAgenceEnKmAuPlusRapide = RESOLVER_HOOK_MAP[
    ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusRapide
  ]({
    enabled: !!(
      enabled &&
      requiredCommands?.includes(
        ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusRapide,
      )
    ),
  });

  const allResolvers = {
    [ResolverEnum.DateCourante]: dateCourante,
    [ResolverEnum.DateHeureCourante]: dateHeureCourante,
    [ResolverEnum.DevisAcceptationCgv]: devisAcceptationCgv,
    [ResolverEnum.DevisInterlocuteur]: devisInterlocuteur,
    [ResolverEnum.DevisEmetteur]: devisEmetteur,
    [ResolverEnum.DevisHonoraires]: devisHonoraires,
    [ResolverEnum.DevisLieuDate]: devisLieuDate,
    [ResolverEnum.DevisAvecModalites]: devisAvecModalites,
    [ResolverEnum.DevisAvecNota]: devisAvecNota,
    [ResolverEnum.DevisModalites]: devisModalites,
    [ResolverEnum.DevisNota]: devisNota,
    [ResolverEnum.DevisObjet]: devisObjet,
    [ResolverEnum.DevisPolitesseIntro]: devisPolitesseIntro,
    [ResolverEnum.DevisPolitesseOutro]: devisPolitesseOutro,
    [ResolverEnum.DevisPrestations]: devisPrestations,
    [ResolverEnum.DevisPrestationsTableau]: devisPrestationsTableau,
    [ResolverEnum.DevisReference]: devisReference,
    [ResolverEnum.DevisReglement]: devisReglement,
    [ResolverEnum.DevisSignatureGerant]: devisSignatureGerant,
    [ResolverEnum.DocIdentifiant]: docIdentifiant,
    [ResolverEnum.DocIncrementGeneration]: docIncrementGeneration,
    [ResolverEnum.DocNombreTotalPages]: docNombreTotalPages,
    [ResolverEnum.DocTableDesMatieres]: docTableDesMatieres,
    [ResolverEnum.DocTitre]: docTitre,
    [ResolverEnum.MissionTitre]: missionTitre,
    [ResolverEnum.SigCarteLieuMission]: sigCarteLieuMission,
    [ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusCourt]:
      sigDistanceArrondieMissionAgenceEnKmAuPlusCourt,
    [ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusLong]:
      sigDistanceArrondieMissionAgenceEnKmAuPlusLong,
    [ResolverEnum.SigDistanceArrondieMissionAgenceEnKmAuPlusRapide]:
      sigDistanceArrondieMissionAgenceEnKmAuPlusRapide,
    [ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusCourt]:
      sigDistanceExacteMissionAgenceEnKmAuPlusCourt,
    [ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusLong]:
      sigDistanceExacteMissionAgenceEnKmAuPlusLong,
    [ResolverEnum.SigDistanceExacteMissionAgenceEnKmAuPlusRapide]:
      sigDistanceExacteMissionAgenceEnKmAuPlusRapide,
  };

  Object.entries(allResolvers).forEach(([key, resolver]) => {
    const resolverConfig = resolversCodeMap[key as ResolverEnum];
    invariant(!!resolverConfig, `Resolver config for ${key} must exist`);
    const { returnTypeId, isResolvableOnFront } = resolverConfig;

    invariant(isResolvableOnFront, 'Resolver must be resolvable on front');

    if (!resolver.isSuccess) {
      return;
    }

    const returnType =
      resolverReturnTypesIdMap[returnTypeId as ResolverReturnTypeId];

    invariant(!!returnType?.code, `Resolver ${key} has an unknown return type`);

    const { data } = ensureDefined(
      { data: resolver.data },
      `Resolver ${key} must return a value`,
    );

    const validationResult = validateResolverReturnType({
      returnTypeCode: returnType.code,
      value: data.generated,
      checkCurrencyId: (id) => checkIfIsId(id) && id in currenciesIdMap,
      checkUnitId: (id) => checkIfIsId(id) && id in unitsIdMap,
      checkVatId: (id) => checkIfIsId(id) && id in vatsIdMap,
    });

    invariant(
      validationResult.isValid,
      `Resolver ${key}: expected ${validationResult.expectedType}, got ${validationResult.actualType}`,
    );
  });

  return {
    allResolvers,
    requiredCommands,
    setRequiredCommands,
  };
};
